"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { SearchIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { LocationSelector } from "@/features/location-selector"

// Add HotKeywords component as a prop
export function GlobalSearch({
  hotKeywords,
}: {
  hotKeywords: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      searchInputRef.current &&
      !searchInputRef.current.contains(event.target as Node)
    ) {
      setOpen(false)
    }
  }, [])

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside)
    } else {
      document.removeEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [open, handleClickOutside])

  const handleOverlayClick = useCallback(() => {
    setOpen(false)
  }, [])

  return (
    <>
      {/* Location selector */}
      <LocationSelector />

      {/* Search container */}
      <div
        className={cn(
          "flex transition-all duration-300 ease-search-button flex-1 flex-col min-w-[150px]",
          open &&
            "lg:min-w-[600px] md:min-w-[400px] sm:min-w-[300px] min-w-0 self-start"
        )}
        data-testid="global-search-wrapper"
      >
        {/* Backdrop overlay when search is open */}
        {open && (
          <div
            className="h-screen bg-[rgba(0,0,0,0.08)] z-[4] fixed top-0 left-0 right-0 overflow-auto transition-all duration-300"
            onClick={handleOverlayClick}
            aria-hidden="true"
            data-testid="global-search-overlay"
          />
        )}

        {/* Search input container */}
        <div
          role="search"
          className={cn(
            "rounded-t-2xl px-3 flex-1",
            open && "z-[4] bg-white mt-[1px] pt-3"
          )}
        >
          <label htmlFor="search" className="sr-only">
            Tìm món trên beFood
          </label>
          <div className="transition-[min-width,max-width] duration-400 ease-linear bg-[#f3f3f3] h-9 flex items-center rounded-full font-medium text-sm text-gray-900">
            <div className="ml-4 mr-3 shrink-0 self-center">
              <SearchIcon className="h-4 w-4" />
            </div>
            <input
              ref={searchInputRef}
              type="search"
              name="search"
              id="search"
              placeholder="Tìm món trên beFood"
              className="w-full py-2 pr-2 border-none focus:outline-none focus:ring-0 overflow-ellipsis outline-none text-gray-900 placeholder:text-gray-900 bg-transparent"
              onFocus={() => setOpen(true)}
            />
          </div>
        </div>

        {/* Search results dropdown */}
        <div
          ref={dropdownRef}
          className={cn(
            "w-0 invisible max-h-[0] translate-0 rounded-b-2xl border border-transparent transition-[opacity,visibility] duration-300 ease-search-button overflow-hidden",
            open &&
              "visible opacity-100 max-h-[calc(100vh-180px)] w-full bg-white z-10 shadow-lg overflow-y-auto"
          )}
        >
          <div className="px-3 pt-4 pb-4">
            <div>
              <h3 className="text-sm font-medium mb-2">Tìm kiếm gần đây</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md">
                  <SearchIcon className="h-4 w-4 text-gray-400" />
                  <span className="text-sm">Cơm gà xối mỡ</span>
                </div>
                <div className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md">
                  <SearchIcon className="h-4 w-4 text-gray-400" />
                  <span className="text-sm">Phở bò</span>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <h3 className="text-sm font-medium mb-2">Tìm kiếm phổ biến</h3>
              {/* Render the server component for hot keywords */}
              {hotKeywords}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
